import type { MonPaiementAbonnement } from "../types";
import { methodeExigePreuve, methodeIcon, methodeLabel, periodiciteLabel } from "../data";
import { formatDate, formatFcfa } from "@/utils/format";
import { cn } from "@/utils/cn";
import PaiementStatutBadge from "./PaiementStatutBadge";

interface PaiementMobileCardProps {
  paiement: MonPaiementAbonnement;
  onViewPreuve: (paiement: MonPaiementAbonnement) => void;
}

function Ligne({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="flex items-center gap-1.5 text-xs text-foreground-500">
        <i className={cn(icon, "shrink-0 text-sm text-foreground-400")} aria-hidden="true" />
        {label}
      </dt>
      <dd className="text-right text-sm font-label font-medium text-foreground-950">{value}</dd>
    </div>
  );
}

/** Carte responsive d'un paiement d'abonnement (mobile / petite largeur). */
export default function PaiementMobileCard({ paiement, onViewPreuve }: PaiementMobileCardProps) {
  const afficheReference = paiement.reference || "—";
  const refuse = paiement.statut === "rejete";

  return (
    <article className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
            <i className={cn(methodeIcon(paiement.mode), "text-lg")} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-heading font-semibold text-foreground-950">
              {paiement.formuleNom || "Formule non définie"}
            </p>
            <p className="truncate text-xs text-foreground-500">{methodeLabel(paiement.mode)}</p>
          </div>
        </div>
        <PaiementStatutBadge statut={paiement.statut} />
      </div>

      <dl className="mt-4 flex flex-col gap-2.5 border-t border-background-200 pt-4">
        <Ligne icon="ri-price-tag-3-line" label="Montant" value={formatFcfa(paiement.montant)} />
        <Ligne icon="ri-calendar-2-line" label="Période" value={periodiciteLabel(paiement.periodicite)} />
        <Ligne icon="ri-hashtag" label="Référence" value={afficheReference} />
        <Ligne icon="ri-calendar-line" label="Soumis le" value={formatDate(paiement.dateSoumission)} />
        {paiement.statut === "valide" && paiement.dateValidation && (
          <Ligne icon="ri-calendar-check-line" label="Validé le" value={formatDate(paiement.dateValidation)} />
        )}
      </dl>

      {paiement.statut === "en_attente" && (
        <p className="mt-3 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-xs text-accent-900">
          <i className="ri-hourglass-line mt-0.5 shrink-0" aria-hidden="true" />
          Paiement en attente de validation par notre équipe.
        </p>
      )}

      {refuse && (
        <div className="mt-3 rounded-md border border-primary-200 bg-primary-50/60 px-3 py-2">
          <p className="text-xs font-label font-medium text-primary-800">Motif du refus</p>
          <p className="mt-0.5 text-xs text-primary-900">{paiement.motif || "Aucun motif renseigné"}</p>
        </div>
      )}

      {methodeExigePreuve(paiement.mode) && paiement.preuveUrl && (
        <button
          type="button"
          onClick={() => onViewPreuve(paiement)}
          className="mt-3 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
        >
          <i className="ri-file-image-line text-sm" aria-hidden="true" />
          Voir la preuve
        </button>
      )}
    </article>
  );
}